import React, { useCallback, useEffect, useId, useState } from 'react';
import { Plus, Trash2, Hash, RefreshCw } from 'lucide-react';
import { Affiliate } from '../../types';
import { adminService, AdminChannel } from '../../services/adminService';
import { authService } from '../../services/authService';
import { TableSkeleton } from '../Skeleton';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

/**
 * 관리자 콘솔 '공개 채널' — 공개 채널 생성·보관(삭제). 계열사 선택('전체'/특정)으로 가시 범위를 정한다.
 * 예전엔 AdminConsole 안에 인라인으로 있었고 '공지 발송'과 channels state를 공유했다(2026-11 분리 —
 * 다른 화면처럼 마운트 시 스스로 조회한다).
 */
export const PublicChannelsManager: React.FC = () => {
  const { showToast } = useToast();
  const fid = useId();
  const [channels, setChannels] = useState<AdminChannel[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newChannelName, setNewChannelName] = useState('');
  const [newChannelDesc, setNewChannelDesc] = useState('');
  const [newChannelAffiliate, setNewChannelAffiliate] = useState<string>(''); // '' = 전체 공개
  const [creating, setCreating] = useState(false);
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  /** 채널 삭제 확인 대상 — window.confirm은 Tauri WebView2에서 항상 false라 ConfirmDialog로 대체 */
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string | null } | null>(null);

  // 뱃지·툴팁에는 raw ID(INAOM 등) 대신 라벨을 보여준다. 목록에 없으면 ID 그대로
  const affiliateLabelOf = (id: string) => affiliates.find(a => a.id === id)?.label ?? id;

  const loadChannels = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setChannels(await adminService.getPublicChannels());
    } catch (err) {
      console.error('Failed to load admin channels:', err);
      setLoadError(err instanceof Error ? err.message : '채널을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
    authService.getAffiliates().then(setAffiliates).catch(() => setAffiliates([]));
  }, [loadChannels]);

  const resetForm = () => {
    setNewChannelName('');
    setNewChannelDesc('');
    setNewChannelAffiliate('');
  };

  const handleCreateChannel = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChannelName.trim()) return;

    setCreating(true);
    try {
      const channel = await adminService.createPublicChannel(
        newChannelName.trim(),
        newChannelDesc.trim() || undefined,
        newChannelAffiliate || null
      );
      setChannels(prev => [channel, ...prev]);
      resetForm();
      setShowCreateForm(false);
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || '채널을 만들지 못했습니다.' });
    } finally {
      setCreating(false);
    }
  };

  const performDeleteChannel = async () => {
    if (!deleteTarget) return;
    const { id: channelId } = deleteTarget;
    setDeleteTarget(null);
    try {
      await adminService.deletePublicChannel(channelId);
      setChannels(prev => prev.filter(ch => ch.id !== channelId));
      showToast({ type: 'success', message: '채널을 보관 처리했습니다.' });
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || '채널을 삭제하지 못했습니다.' });
    }
  };

  const activeChannels = channels.filter(ch => !ch.isArchived);

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* Actions bar */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-slate-500">
          공개 채널 {activeChannels.length}개
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={loadChannels}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            새로고침
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus size={14} />
            새 채널
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <form
          onSubmit={handleCreateChannel}
          className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200"
        >
          <h3 className="text-sm font-semibold text-slate-700 mb-3">새 공개 채널 생성</h3>
          <div className="space-y-3">
            <div>
              <label htmlFor={`${fid}-chname`} className="block text-xs text-slate-500 mb-1">채널 이름 *</label>
              <input id={`${fid}-chname`}
                type="text"
                value={newChannelName}
                onChange={(e) => setNewChannelName(e.target.value)}
                placeholder="예: 공지사항, 자유게시판"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor={`${fid}-chdesc`} className="block text-xs text-slate-500 mb-1">설명 (선택)</label>
              <input id={`${fid}-chdesc`}
                type="text"
                value={newChannelDesc}
                onChange={(e) => setNewChannelDesc(e.target.value)}
                placeholder="채널에 대한 간단한 설명"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
            </div>
            <div>
              <label htmlFor={`${fid}-chaff`} className="block text-xs text-slate-500 mb-1">계열사 (가시 범위)</label>
              <select id={`${fid}-chaff`}
                value={newChannelAffiliate}
                onChange={(e) => setNewChannelAffiliate(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              >
                <option value="">전체 공개 (모든 계열사)</option>
                {affiliates.map(a => (
                  <option key={a.id} value={a.id}>{a.label} ({a.id})</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                특정 계열사를 선택하면 해당 계열사 사용자에게만 채널이 보입니다.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
              >
                취소
              </button>
              <button
                type="submit"
                disabled={creating || !newChannelName.trim()}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {creating ? '생성 중...' : '생성'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Error banner */}
      {loadError && (
        <div className="mb-3 px-3 py-2 bg-brand-50 text-brand-700 text-sm rounded border border-brand-200 flex items-center justify-between">
          <span>{loadError}</span>
          <button onClick={loadChannels} className="text-brand-700 underline text-xs ml-2 shrink-0">
            다시 시도
          </button>
        </div>
      )}

      {/* Channel list */}
      {loading && channels.length === 0 ? (
        <TableSkeleton rows={5} cols={3} />
      ) : !loadError && activeChannels.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3"><Hash size={24} /></div>
          <p className="text-sm font-medium text-slate-600">아직 공개 채널이 없습니다</p>
          <p className="text-xs text-slate-400 mt-1">공지·자유게시판처럼 전 직원이 함께 보는 채널을 만들어 보세요</p>
          <button
            type="button"
            onClick={() => setShowCreateForm(true)}
            className="mt-4 inline-flex items-center gap-1.5 px-4 h-10 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors"
          >
            <Plus size={14} />
            새 채널
          </button>
        </div>
      ) : (
        <div className={`space-y-2 ${ADMIN_LIST_FILL}`}>
          {activeChannels.map(channel => (
            <div
              key={channel.id}
              className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-8 w-8 bg-slate-100 rounded-lg flex items-center justify-center shrink-0">
                  <Hash size={16} className="text-slate-500" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">{channel.name}</span>
                    <span
                      className={`shrink-0 px-2 py-0.5 text-xs rounded-full font-medium ${
                        channel.affiliateId
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                      title={channel.affiliateId
                        ? `${affiliateLabelOf(channel.affiliateId)} 전용 채널`
                        : '모든 계열사에 공개된 채널'}
                    >
                      {channel.affiliateId ? affiliateLabelOf(channel.affiliateId) : '전체'}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400 truncate">
                    {channel.description || '설명 없음'}
                    <span className="mx-1">·</span>
                    멤버 {channel.memberCount || 0}명
                  </div>
                </div>
              </div>
              <button
                onClick={() => setDeleteTarget({ id: channel.id, name: channel.name })}
                className="p-2 text-slate-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors shrink-0"
                title="채널 삭제"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="채널 삭제"
        message={`'${deleteTarget?.name ?? ''}' 채널을 삭제(보관)할까요? 채널은 목록에서 사라지고 메시지는 보존됩니다.`}
        confirmLabel="삭제"
        danger
        onConfirm={performDeleteChannel}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};
